/**
 * NavigatorPanel — Elementor-style Layers/Navigator tree.
 *
 * Shows every placed section from both `blob.sections[]` (Visual Builder
 * canvas widgets) and `blob.homepage.sections[]` (customer homepage
 * runtime sections) as a single flat tree with per-item operations:
 *   • rename (double-click / pencil)
 *   • hide / show
 *   • lock / unlock (blocks accidental edits on the canvas)
 *   • duplicate
 *   • delete
 *   • move up / move down
 *   • drag-to-reorder
 *
 * Clicking a row selects the section in the builder — same as clicking
 * on the canvas. Selected rows are highlighted.
 *
 * SAFETY: this panel edits only visual/order fields. Business logic
 * keys (limit, filter_categories, filter_brands, price, stock, etc.)
 * are never touched.
 */
import { useMemo, useState } from "react";
import {
  Layers, Eye, EyeOff, Lock, Unlock, Copy, Trash2, ArrowUp, ArrowDown,
  Pencil, Check, X, GripVertical, ChevronRight, ChevronDown, Sparkles, LayoutTemplate,
} from "lucide-react";
import { moveItem, duplicateItem, removeItem, updateItem } from "@/lib/visualBuilder/layoutTree";
import { componentById } from "@/lib/visualBuilder/registry";

export default function NavigatorPanel({
  platform, blob, selection, onSelect, onChange,
}) {
  const sections = blob.sections || [];
  const homepageSections = blob.homepage?.sections || [];
  const [openHome, setOpenHome] = useState(true);
  const [openCanvas, setOpenCanvas] = useState(true);
  const [editing, setEditing] = useState(null); // { scope, idx }
  const [editVal, setEditVal] = useState("");
  const [dragIdx, setDragIdx] = useState(null);
  const [dropTarget, setDropTarget] = useState(null); // {scope, idx}

  // ── mutators ──────────────────────────────────────────────────────
  const commitCanvas = (next) => onChange({ sections: next });
  const commitHome = (next) => onChange({ homepage: { ...(blob.homepage || {}), sections: next } });

  const dupSection = (scope, idx) => {
    if (scope === "canvas") commitCanvas(duplicateItem(sections, idx, { keyField: "id" }));
    else commitHome(duplicateItem(homepageSections, idx, { keyField: "key" }));
  };
  const delSection = (scope, idx) => {
    if (scope === "canvas") commitCanvas(removeItem(sections, idx));
    else commitHome(removeItem(homepageSections, idx));
  };
  const move = (scope, idx, dir) => {
    const list = scope === "canvas" ? sections : homepageSections;
    const to = idx + dir;
    if (to < 0 || to >= list.length) return;
    if (scope === "canvas") commitCanvas(moveItem(sections, idx, to));
    else commitHome(moveItem(homepageSections, idx, to));
  };
  const patch = (scope, idx, upd) => {
    if (scope === "canvas") commitCanvas(updateItem(sections, idx, upd));
    else commitHome(updateItem(homepageSections, idx, upd));
  };

  const startRename = (scope, idx, currentLabel) => {
    setEditing({ scope, idx });
    setEditVal(currentLabel || "");
  };
  const commitRename = () => {
    if (!editing) return;
    patch(editing.scope, editing.idx, { label: editVal });
    setEditing(null);
  };

  // ── DnD ───────────────────────────────────────────────────────────
  const onRowDragStart = (scope, idx) => (e) => {
    setDragIdx({ scope, idx });
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/vb-nav", `${scope}:${idx}`);
    } catch { /* noop */ }
  };
  const onRowDragOver = (scope, idx) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTarget({ scope, idx });
  };
  const onRowDrop = (scope, idx) => (e) => {
    e.preventDefault();
    const from = dragIdx;
    setDragIdx(null); setDropTarget(null);
    if (!from || from.scope !== scope) return;
    if (from.idx === idx) return;
    if (scope === "canvas") commitCanvas(moveItem(sections, from.idx, idx));
    else commitHome(moveItem(homepageSections, from.idx, idx));
  };
  const onRowDragEnd = () => { setDragIdx(null); setDropTarget(null); };

  // ── Render ────────────────────────────────────────────────────────
  const homeRows = useMemo(() => homepageSections.map((s, idx) => ({
    id: s.key, idx, scope: "home", label: s.label || s.key, key: s.key,
    hidden: s.enabled === false, locked: !!s.locked,
  })), [homepageSections]);

  const canvasRows = useMemo(() => sections.map((s, idx) => ({
    id: s.id, idx, scope: "canvas", label: s.label || componentById(platform, s.comp)?.label || s.comp,
    key: s.comp, hidden: !!s.hidden, locked: !!s.locked,
  })), [sections, platform]);

  return (
    <div className="border-t border-slate-100 bg-slate-50/50" data-testid="vb-navigator">
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-100 bg-white">
        <Layers className="w-3.5 h-3.5 text-slate-500" />
        <div className="text-[10.5px] uppercase tracking-widest font-bold text-slate-500 flex-1">Layers · Navigator</div>
        <span className="text-[10px] text-slate-400 tabular-nums">{homepageSections.length + sections.length}</span>
      </div>

      <div className="max-h-[36vh] overflow-y-auto">
        <TreeGroup
          label="Homepage Sections"
          icon={Sparkles}
          open={openHome}
          onToggle={() => setOpenHome(!openHome)}
          count={homepageSections.length}
        />
        {openHome && homeRows.map((r) => (
          <TreeRow
            key={`h-${r.id}`}
            row={r}
            selected={false}
            editing={editing?.scope === "home" && editing.idx === r.idx}
            editVal={editVal}
            setEditVal={setEditVal}
            onStartRename={() => startRename("home", r.idx, r.label)}
            onCommitRename={commitRename}
            onCancelRename={() => setEditing(null)}
            onSelect={() => {}}
            onToggleHide={() => patch("home", r.idx, { enabled: r.hidden ? true : false })}
            onToggleLock={() => patch("home", r.idx, { locked: !r.locked })}
            onDup={() => dupSection("home", r.idx)}
            onDel={() => delSection("home", r.idx)}
            onUp={() => move("home", r.idx, -1)}
            onDown={() => move("home", r.idx, 1)}
            onDragStart={onRowDragStart("home", r.idx)}
            onDragOver={onRowDragOver("home", r.idx)}
            onDrop={onRowDrop("home", r.idx)}
            onDragEnd={onRowDragEnd}
            dragging={dragIdx?.scope === "home" && dragIdx.idx === r.idx}
            dropHere={dropTarget?.scope === "home" && dropTarget.idx === r.idx}
          />
        ))}

        <TreeGroup
          label="Canvas Widgets"
          icon={LayoutTemplate}
          open={openCanvas}
          onToggle={() => setOpenCanvas(!openCanvas)}
          count={sections.length}
        />
        {openCanvas && canvasRows.map((r) => (
          <TreeRow
            key={`c-${r.id}`}
            row={r}
            selected={selection === r.key}
            editing={editing?.scope === "canvas" && editing.idx === r.idx}
            editVal={editVal}
            setEditVal={setEditVal}
            onStartRename={() => startRename("canvas", r.idx, r.label)}
            onCommitRename={commitRename}
            onCancelRename={() => setEditing(null)}
            onSelect={() => onSelect?.(r.key)}
            onToggleHide={() => patch("canvas", r.idx, { hidden: !r.hidden })}
            onToggleLock={() => patch("canvas", r.idx, { locked: !r.locked })}
            onDup={() => dupSection("canvas", r.idx)}
            onDel={() => delSection("canvas", r.idx)}
            onUp={() => move("canvas", r.idx, -1)}
            onDown={() => move("canvas", r.idx, 1)}
            onDragStart={onRowDragStart("canvas", r.idx)}
            onDragOver={onRowDragOver("canvas", r.idx)}
            onDrop={onRowDrop("canvas", r.idx)}
            onDragEnd={onRowDragEnd}
            dragging={dragIdx?.scope === "canvas" && dragIdx.idx === r.idx}
            dropHere={dropTarget?.scope === "canvas" && dropTarget.idx === r.idx}
          />
        ))}

        {homeRows.length === 0 && canvasRows.length === 0 && (
          <div className="p-4 text-[11px] text-slate-400 text-center">
            No layers yet. Drag components from the palette above.
          </div>
        )}
      </div>
    </div>
  );
}

function TreeGroup({ label, icon: Icon, open, onToggle, count }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-white text-[11px] font-semibold text-slate-600 border-b border-slate-100"
    >
      {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
      <Icon className="w-3 h-3 text-slate-400" />
      <span className="flex-1">{label}</span>
      <span className="text-[10px] text-slate-400 tabular-nums">{count}</span>
    </button>
  );
}

function TreeRow({
  row, selected, editing, editVal, setEditVal,
  onStartRename, onCommitRename, onCancelRename,
  onSelect, onToggleHide, onToggleLock, onDup, onDel, onUp, onDown,
  onDragStart, onDragOver, onDrop, onDragEnd, dragging, dropHere,
}) {
  return (
    <div
      className={`group flex items-center gap-1 px-2 py-1 border-b border-slate-100 transition-colors ${
        selected ? "bg-[#FFF3F4]" : "bg-white hover:bg-slate-50"
      } ${row.hidden ? "opacity-55" : ""} ${row.locked ? "" : ""} ${dragging ? "opacity-40" : ""} ${
        dropHere ? "ring-1 ring-[#E32636]" : ""
      }`}
      draggable={!row.locked}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      data-testid={`vb-nav-row-${row.id}`}
    >
      <GripVertical className={`w-3 h-3 ${row.locked ? "text-slate-200" : "text-slate-400"} shrink-0 cursor-grab`} />

      {editing ? (
        <div className="flex-1 flex items-center gap-1">
          <input
            autoFocus
            value={editVal}
            onChange={(e) => setEditVal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") onCommitRename();
              if (e.key === "Escape") onCancelRename();
            }}
            className="h-6 px-1.5 text-[11px] flex-1 rounded border border-[#E32636] outline-none"
            data-testid={`vb-nav-rename-input-${row.id}`}
          />
          <button type="button" onClick={onCommitRename} className="w-5 h-5 grid place-items-center rounded bg-emerald-500 text-white">
            <Check className="w-3 h-3" />
          </button>
          <button type="button" onClick={onCancelRename} className="w-5 h-5 grid place-items-center rounded bg-slate-300 text-white">
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={onSelect}
          onDoubleClick={onStartRename}
          className="flex-1 min-w-0 text-left"
          data-testid={`vb-nav-select-${row.id}`}
        >
          <div className={`text-[12px] font-semibold truncate ${selected ? "text-[#E32636]" : "text-slate-800"}`}>{row.label}</div>
          <div className="text-[9.5px] font-mono text-slate-400 truncate">{row.key}</div>
        </button>
      )}

      <div className="hidden group-hover:flex items-center gap-0.5">
        <IconBtn title="Rename"   onClick={onStartRename} testid={`vb-nav-rename-${row.id}`}><Pencil className="w-3 h-3" /></IconBtn>
        <IconBtn title="Move Up"   onClick={onUp}          testid={`vb-nav-up-${row.id}`}><ArrowUp className="w-3 h-3" /></IconBtn>
        <IconBtn title="Move Down" onClick={onDown}        testid={`vb-nav-down-${row.id}`}><ArrowDown className="w-3 h-3" /></IconBtn>
        <IconBtn title="Duplicate" onClick={onDup}         testid={`vb-nav-dup-${row.id}`}><Copy className="w-3 h-3" /></IconBtn>
        <IconBtn title="Delete"    onClick={onDel} danger  testid={`vb-nav-del-${row.id}`}><Trash2 className="w-3 h-3" /></IconBtn>
      </div>
      <IconBtn
        title={row.locked ? "Unlock" : "Lock"}
        onClick={onToggleLock}
        testid={`vb-nav-lock-${row.id}`}
        active={row.locked}
      >{row.locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}</IconBtn>
      <IconBtn
        title={row.hidden ? "Show" : "Hide"}
        onClick={onToggleHide}
        testid={`vb-nav-vis-${row.id}`}
        active={row.hidden}
      >{row.hidden ? <EyeOff className="w-3 h-3 text-red-500" /> : <Eye className="w-3 h-3" />}</IconBtn>
    </div>
  );
}

function IconBtn({ children, title, onClick, testid, danger, active }) {
  return (
    <button
      type="button" title={title} onClick={onClick}
      data-testid={testid}
      className={`w-5 h-5 grid place-items-center rounded ${
        danger ? "text-red-500 hover:bg-red-50" :
        active ? "text-[#E32636] bg-[#FFF3F4]" :
                 "text-slate-500 hover:bg-slate-100"
      }`}
    >{children}</button>
  );
}
